﻿import { getToken, onMessage, type MessagePayload } from 'firebase/messaging'
import { getFirebaseMessaging, VAPID_KEY, firebaseConfig } from '../../../lib/firebase'
import { registerDeviceTokenApi } from '../api/deviceTokensApi'

export const REGISTERED_FCM_TOKEN_KEY = 'school_erp_registered_fcm_token'
export const REGISTERED_FCM_USER_KEY = 'school_erp_registered_fcm_user'

let isRegistrationInProgress = false

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'denied'
  }
  return await Notification.requestPermission()
}

export async function registerFcmWebPushDeviceToken(userId?: string): Promise<string | null> {
  if (
    typeof window === 'undefined' ||
    !('Notification' in window) ||
    !('serviceWorker' in navigator)
  ) {
    return null
  }

  if (isRegistrationInProgress) {
    return null
  }

  try {
    isRegistrationInProgress = true

    // 1. Request notification permission if not already decided
    let permission = Notification.permission
    if (permission === 'default') {
      permission = await Notification.requestPermission()
    }

    if (permission !== 'granted') {
      return null
    }

    if (!VAPID_KEY) {
      console.warn('VITE_FIREBASE_VAPID_KEY is not configured.')
      return null
    }

    // 2. Register/Retrieve Service Worker for FCM
    const swUrl = `/firebase-messaging-sw.js?apiKey=${encodeURIComponent(
      firebaseConfig.apiKey
    )}&projectId=${encodeURIComponent(
      firebaseConfig.projectId
    )}&messagingSenderId=${encodeURIComponent(
      firebaseConfig.messagingSenderId
    )}&appId=${encodeURIComponent(firebaseConfig.appId)}`

    const serviceWorkerRegistration = await navigator.serviceWorker.register(swUrl, {
      scope: '/',
    })

    await navigator.serviceWorker.ready

    // 3. Initialize Firebase Messaging
    const messaging = await getFirebaseMessaging()
    if (!messaging) {
      return null
    }

    // 4. Obtain real FCM Token
    const currentToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration,
    })

    if (!currentToken) {
      return null
    }

    // 5. Prevent redundant duplicate registration requests
    const cachedToken = window.localStorage.getItem(REGISTERED_FCM_TOKEN_KEY)
    const cachedUser = window.localStorage.getItem(REGISTERED_FCM_USER_KEY)

    if (cachedToken === currentToken && cachedUser === (userId || '')) {
      return currentToken
    }

    // 6. Call POST /api/device-tokens with exact backend contract
    await registerDeviceTokenApi({
      fcmToken: currentToken,
      deviceType: 'WEB',
    })

    // 7. Cache registered token state upon success
    window.localStorage.setItem(REGISTERED_FCM_TOKEN_KEY, currentToken)
    if (userId) {
      window.localStorage.setItem(REGISTERED_FCM_USER_KEY, userId)
    }

    return currentToken
  } catch (error) {
    console.error('Failed to register FCM device token:', error)
    return null
  } finally {
    isRegistrationInProgress = false
  }
}

export async function subscribeToForegroundMessages(
  onNotificationReceived?: (payload: MessagePayload) => void
): Promise<(() => void) | null> {
  const messaging = await getFirebaseMessaging()
  if (!messaging) return null

  try {
    const unsubscribe = onMessage(messaging, (payload) => {
      onNotificationReceived?.(payload)
    })
    return unsubscribe
  } catch (error) {
    console.warn('Failed to subscribe to foreground messages:', error)
    return null
  }
}
